import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import AbbrTip from '@/components/AbbrTip';
import { useLearnStockModal } from './learnStockModalContext';

const FAQS = [
  {
    q: 'What is a currency-hedged ETF and why does it exist?',
    a: 'A currency-hedged ETF buys foreign assets (Japanese stocks, European bonds, etc.) and simultaneously enters USD/foreign-currency forward contracts that neutralize exchange-rate movement. The point is to isolate the local-market return — so if you buy DBEF (hedged EAFE), you capture what the underlying European and Japanese stocks did in their home currencies, WITHOUT the dollar-vs-yen or dollar-vs-euro swing polluting your return. Unhedged versions (like EFA) leave the currency component alive, meaning your return is stocks + FX.',
  },
  {
    q: 'When does a hedged ETF outperform an unhedged version of the same fund?',
    a: 'When the U.S. dollar is STRENGTHENING against the foreign currency. If EFA holds €100 of stocks and the euro drops 8% against the USD, EFA loses 8% just on currency translation before any stock movement. DBEF holds the same €100 but its forward contract profits by ~8%, cancelling the FX drag. Rule of thumb: DXY (dollar index) up → hedged wins. DXY down → unhedged wins.',
  },
  {
    q: 'Why does duration matter for international bond funds?',
    a: 'Duration measures interest-rate sensitivity in years. For every 1% move in rates, the fund\'s price moves roughly its duration in the opposite direction. Broad international bond funds (BNDX, BWX) have 7-9 year durations — a 1% global rate rise drops them 7-9%. Short-duration funds (ISHG, 1-3 year maturities) have ~2 year duration — a 1% rate rise drops them only 2%. When you add UNHEDGED to that, FX movement dominates ISHG\'s tiny rate exposure, making it more of a "short-the-dollar" tactical vehicle than a real bond allocation.',
  },
  {
    q: 'What tax form should I expect for currency-hedged ETFs?',
    a: 'The overwhelming majority — DBEF, HEFA, DXJ, HEDJ, DBEU, HEZU, DBEM, HEEM, DDWM, HAWX, DBAW, DBJP, HEWJ, DBEZ, HSCZ, LVHI, HFXI, DDLS, RFDI, RFEM, FLJH, JHDG, DHDG, EMD, GLDW — issue standard Form 1099-DIV. Long-term gains taxed 0/15/20%, short-term at your ordinary bracket, distributions may include qualified-dividend income if the underlying stocks meet the qualified holding period. A minority (some Invesco DB Currency series, WisdomTree currency-strategy funds) that use Section 1256 futures inside pass through 60/40 blended tax treatment via Form 6781. Always check the fund\'s year-end tax reporting page before assuming.',
  },
  {
    q: 'How does DiviDrip know which benchmarks to compare my international ETF against?',
    a: 'The Insights-tab Benchmark Performance card runs a server-side classifier that reads the ticker\'s name, description, and asset type. If it detects an international / EM / bond / REIT / FX ETF, it AUTOMATICALLY swaps the default SPY/SCHD/VYM trio for a family-appropriate one. Example: EMXC (EM ex-China) auto-routes to EMXC/XCEM/IEMG. DBEF (hedged EAFE) auto-routes to DBEF/HEFA/EFA. FXB (British Pound) auto-routes to UUP/ELD/CEW. You\'ll see a "Compared against: [Category]" caption above the trio explaining what family was detected.',
  },
  {
    q: 'Should I own hedged AND unhedged versions of the same market?',
    a: 'Some institutional allocators do a 50/50 split so they capture half the FX benefit when the USD is weak and half the FX protection when the USD is strong. For most retail investors it\'s over-engineering. A cleaner rule: for core long-term allocations, HEDGED (isolates asset performance); for tactical shorter-term bets on a specific dollar cycle, UNHEDGED. Bond allocations lean strongly toward hedged because hedging costs are minor relative to the small bond yields — an 8% FX swing wipes out three years of a 2.5% bond coupon.',
  },
  {
    q: 'Why did DBV disappear from every FX-strategy benchmark list?',
    a: 'DBV (Invesco DB G10 Currency Harvest ETF) was liquidated in March 2023 due to low assets. Some older investment guides still quote DBV as the "systematic G10 FX carry benchmark", but it hasn\'t traded in three years. DiviDrip replaced DBV with UUP (Invesco DB US Dollar Index Bullish Fund) in the FX-strategy trio — UUP is still actively trading and captures the same "long-USD-vs-basket" concept that made DBV useful.',
  },
];

// Feb 2026 — Filter-group tags per ticker so the chips above the
// table can narrow the 27-name list. A ticker can belong to multiple
// groups (e.g. HAWX is hedged AND covers both EAFE and EM baskets).
const DIV_1099_TICKERS = [
  ['DBEF', 'Xtrackers MSCI EAFE Hedged Equity',              ['hedged', 'eafe']],
  ['HEFA', 'iShares Currency Hedged MSCI EAFE',              ['hedged', 'eafe']],
  ['DXJ',  'WisdomTree Japan Hedged Equity',                 ['hedged', 'eafe']],
  ['LVHI', 'Franklin Intl Low Vol High Div Idx',             ['unhedged', 'eafe']],
  ['HFXI', 'IQ FTSE Intl Equity Currency Neutral',           ['hedged', 'eafe']],
  ['HEDJ', 'WisdomTree Europe Hedged Equity',                ['hedged', 'eafe']],
  ['CLOI', 'VanEck CLO ETF',                                 ['unhedged']],
  ['DDWM', 'WisdomTree Dyn Intl Equity',                     ['hedged', 'eafe']],
  ['DFSI', 'Dimensional Intl Sustainability Core 1',         ['unhedged', 'eafe']],
  ['DBEU', 'Xtrackers MSCI Europe Hedged Equity',            ['hedged', 'eafe']],
  ['HEWJ', 'iShares Currency Hedged MSCI Japan',             ['hedged', 'eafe']],
  ['DBJP', 'Xtrackers MSCI Japan Hedged Equity',             ['hedged', 'eafe']],
  ['DFSE', 'Dimensional EM Sustainability Core 1',           ['unhedged', 'em']],
  ['HEZU', 'iShares Currency Hedged MSCI Eurozone',          ['hedged', 'eafe']],
  ['DDLS', 'WisdomTree Dyn Intl SmallCap Equity',            ['hedged', 'eafe']],
  ['HAWX', 'iShares Currency Hedged MSCI ACWI ex US',        ['hedged', 'eafe', 'em']],
  ['DBAW', 'Xtrackers MSCI ACWI ex US Hedged',               ['hedged', 'eafe', 'em']],
  ['HSCZ', 'iShares Currency Hedged MSCI EAFE Small Cap',    ['hedged', 'eafe']],
  ['FLJH', 'Franklin FTSE Japan Hedged',                     ['hedged', 'eafe']],
  ['RFDI', 'First Trust RiverFront Dyn Dev Intl',            ['hedged', 'eafe']],
  ['DBEM', 'Xtrackers MSCI EM Hedged Equity',                ['hedged', 'em']],
  ['JHDG', 'John Hancock Hedged Equity',                     ['hedged']],
  ['RFEM', 'First Trust RiverFront Dyn EM',                  ['hedged', 'em']],
  ['DBEZ', 'Xtrackers MSCI Eurozone Hedged Equity',          ['hedged', 'eafe']],
  ['DHDG', 'FT Vest U.S. Equity Quarterly Buffer',           ['unhedged']],
  ['EMD',  'Goldman Sachs Access EM USD Bond',               ['unhedged', 'em']],
  ['GLDW', 'Roundhill Gold WeeklyPay',                       ['unhedged']],
];

const FILTER_CHIPS = [
  { key: 'all',      label: 'All',      def: null },
  {
    key: 'hedged',
    label: 'Hedged',
    def: 'Foreign-holding ETFs that layer in USD/foreign-currency forward contracts to neutralize FX. You capture the underlying local-market return only — no yen/euro/pound swing polluting your P&L. Wins when the US dollar strengthens.',
    anchor: '/glossary#entry-hedged-vs-unhedged-etfs',
  },
  {
    key: 'unhedged',
    label: 'Unhedged',
    def: 'Foreign-holding ETFs that leave the currency component alive — your return is stocks + FX. Wins when the US dollar weakens; used tactically by dollar-bear allocators or as the low-fee default in most international index funds.',
    anchor: '/glossary#entry-hedged-vs-unhedged-etfs',
  },
  {
    key: 'em',
    label: 'EM',
    def: 'Emerging Markets — funds tracking developing-economy stock/bond indices (China, India, Brazil, Taiwan, South Korea, Mexico, etc.). Higher volatility and higher expected return than developed-markets peers; often paired with an ex-China variant to isolate geopolitical risk.',
    anchor: '/glossary#entry-emerging-markets-ex-china-etf',
  },
  {
    key: 'eafe',
    label: 'EAFE',
    def: 'Europe, Australasia, and the Far East — the standard MSCI benchmark for developed-market equities OUTSIDE North America. Covers 21 countries including Japan, UK, France, Germany, Switzerland, Australia. The single most-benchmarked international index in the world.',
    anchor: '/glossary#entry-hedged-vs-unhedged-etfs',
  },
];

// Feb 2026 — Rendered as a CHILD of <LearnPage>, so its
// useLearnStockModal() call resolves to the real provider (LearnPage
// wraps its children in LearnStockModalContext.Provider). Called at
// the top of LearnHedgedVsUnhedgedEtfs directly would return the noop
// default because that component is the PARENT of the Provider.
function Div1099Table() {
  const { openLearnStock } = useLearnStockModal();
  const [activeFilter, setActiveFilter] = useState('all');
  // Feb 2026 — Which chip's "what is this?" popover is currently open,
  // or null for none. Only one popover is open at a time. Dismissal
  // fires on any click OR Escape keypress (see effect below).
  const [infoOpen, setInfoOpen] = useState(null);
  useEffect(() => {
    if (!infoOpen) return undefined;
    const dismiss = () => setInfoOpen(null);
    // 50ms guard so the click that just OPENED the popover doesn't
    // instantly close it via bubble-up.
    const t = setTimeout(() => {
      window.addEventListener('click', dismiss, { once: true });
      window.addEventListener('keydown', dismiss, { once: true });
    }, 50);
    return () => {
      clearTimeout(t);
      window.removeEventListener('click', dismiss);
      window.removeEventListener('keydown', dismiss);
    };
  }, [infoOpen]);
  const rows = activeFilter === 'all'
    ? DIV_1099_TICKERS
    : DIV_1099_TICKERS.filter(([, , groups]) => (groups || []).includes(activeFilter));
  return (
    <div className="not-prose my-4 space-y-2">
      {/* Filter chips — "All" resets to the full 27-ticker list. Each
          non-All chip carries a small "?" button that toggles a
          definition popover above it. */}
      <div className="flex flex-wrap items-center gap-1.5" data-testid="learn-hedged-filter-chips">
        {FILTER_CHIPS.map((chip) => {
          const isActive = activeFilter === chip.key;
          const infoShown = infoOpen === chip.key;
          return (
            <div key={chip.key} className="relative inline-flex items-center">
              <button
                type="button"
                onClick={() => setActiveFilter(chip.key)}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider ring-1 transition-colors ${
                  isActive
                    ? 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 ring-cyan-500/50'
                    : 'bg-muted/40 text-muted-foreground ring-border/60 hover:bg-muted/70 hover:text-foreground'
                }`}
                data-testid={`learn-hedged-filter-${chip.key}`}
                aria-pressed={isActive}
              >
                {chip.label}
                {chip.def && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      setInfoOpen(infoShown ? null : chip.key);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        e.preventDefault();
                        setInfoOpen(infoShown ? null : chip.key);
                      }
                    }}
                    className="w-3.5 h-3.5 inline-flex items-center justify-center rounded-full bg-current/10 text-[9px] font-black opacity-70 hover:opacity-100 cursor-help"
                    aria-label={`What does "${chip.label}" mean?`}
                    data-testid={`learn-hedged-filter-${chip.key}-info-trigger`}
                  >
                    ?
                  </span>
                )}
              </button>
              {infoShown && chip.def && (
                <div
                  className="absolute bottom-full left-0 mb-2 w-72 sm:w-80 z-50 rounded-lg border-2 border-cyan-500/40 bg-popover text-popover-foreground shadow-2xl p-3 text-[12px] leading-snug normal-case tracking-normal font-normal"
                  role="dialog"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`learn-hedged-filter-${chip.key}-info`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="text-[11px] uppercase font-bold text-cyan-700 dark:text-cyan-300 tracking-wider">
                      {chip.label}
                    </div>
                    <button
                      type="button"
                      onClick={() => setInfoOpen(null)}
                      className="text-muted-foreground hover:text-foreground text-sm font-bold leading-none"
                      aria-label="Close"
                      data-testid={`learn-hedged-filter-${chip.key}-info-close`}
                    >
                      ×
                    </button>
                  </div>
                  <p className="text-foreground/90 mb-2">{chip.def}</p>
                  {chip.anchor && (
                    <a
                      href={chip.anchor}
                      target="divdrip-glossary"
                      /* Feb 2026 — Same-origin glossary link. Chrome
                         disables named-target tab reuse when
                         `rel="noopener"` is set, which is why the
                         earlier version opened a fresh tab every
                         click. Dropping the rel attribute lets a
                         second click navigate the existing tab —
                         matching the AbbrTip pattern used on the
                         Capital Appreciation Playbook guide. */
                      className="text-[11px] font-semibold text-cyan-700 dark:text-cyan-300 hover:underline"
                    >
                      Read full definition →
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <span className="ml-1 text-[11px] text-muted-foreground" data-testid="learn-hedged-filter-count">
          {rows.length} of {DIV_1099_TICKERS.length}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="learn-hedged-1099div-table">
          <thead className="text-muted-foreground uppercase tracking-wide text-[10px]">
            <tr>
              <th className="text-left py-2 pr-3">Ticker</th>
              <th className="text-left py-2 pr-3">Fund</th>
              <th className="text-left py-2 pr-3">LT rate</th>
              <th className="text-left py-2 pr-3">ST rate</th>
              <th className="text-left py-2 pr-3">Form</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {rows.map(([tk, nm]) => (
              <tr key={tk} className="border-b border-border/30">
                <td className="py-1 pr-3 font-bold">
                  <button
                    type="button"
                    onClick={() => openLearnStock(tk)}
                    className="font-bold font-mono text-cyan-700 dark:text-cyan-300 hover:text-amber-600 dark:hover:text-amber-400 underline decoration-dotted underline-offset-2 transition-colors"
                    data-testid={`learn-hedged-ticker-${tk}`}
                    title={`Open ${tk} — ${nm} — in the Stock Modal without leaving this guide`}
                  >
                    {tk}
                  </button>
                </td>
                <td className="py-1 pr-3 font-sans">{nm}</td>
                <td className="py-1 pr-3">20%</td>
                <td className="py-1 pr-3">≤37%</td>
                <td className="py-1 pr-3">1099-DIV</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="py-3 text-center text-muted-foreground font-sans">
                  No funds in this category — try a different chip.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function LearnHedgedVsUnhedgedEtfs() {
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };
  return (
    <LearnPage
      title="Hedged vs Unhedged ETFs — Currency, Bonds, and the Tax Wrinkles | DiviDrip"
      description="The single most important structural distinction for any international / EM / bond ETF. How the FX hedge works, when hedged wins vs when unhedged wins, single-currency trusts (FXA/FXB/FXE/FXY), Section 1256 vs 1099-DIV tax treatment, and DiviDrip's family-benchmark routing that auto-picks the right comparison ETFs."
      canonical="https://twylightcrow.com/learn/hedged-vs-unhedged-etfs"
      guidePath="/learn/hedged-vs-unhedged-etfs"
      sectionLabel="Vehicles & Structures"
      sectionAnchor="vehicles"
      kicker="Learn · International vehicles"
      h1="Hedged vs Unhedged ETFs — Currency, Bonds, and the Tax Wrinkles"
      datePublished="2026-02-20"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/ex-us-dividend-aristocrats', label: 'Ex-US Dividend Aristocrats — UK / Canadian ADRs' },
        { to: '/learn/international-dividends', label: 'International dividends — tax and currency basics' },
        { to: '/learn/reits-vs-bdcs-vs-cefs', label: 'REITs vs BDCs vs CEFs' },
        { to: '/learn/preferred-stocks-explained', label: 'Preferred Stocks explained' },
        { to: '/learn/bond-yields-vs-dividend-yields', label: 'Bond yields vs dividend yields' },
      ]}
    >
      <p>
        When you buy an S&amp;P 500 ETF like SPY, your return has one moving part — how U.S. stocks did. When you buy an <em>international</em>, <em>emerging-markets</em>, or <em>foreign-bond</em> ETF, your return has TWO moving parts: (1) how the underlying assets did in their local currency, and (2) how those local currencies moved against the U.S. dollar. That second part — the moment-to-moment price of one currency measured against another — is called <strong>FX</strong> (short for <em>foreign exchange</em>), and its swings can double your gains or cancel them entirely, sometimes in the same quarter. The choice between a <strong>currency-hedged</strong> ETF and an <strong>unhedged</strong> version of the same market is what decides whether you own that FX volatility or opt out of it.
      </p>

      <h2>The FX hedge in one paragraph</h2>
      <p>
        A currency-hedged ETF does exactly two things: (a) buys the foreign asset — the same Japanese, European, or emerging-market securities its unhedged twin holds; (b) enters a rolling monthly <em>FX forward contract</em> that legally locks in a predetermined exchange rate for a slice of the fund&rsquo;s notional value back to USD. If the yen drops 5% against the dollar, the yen-denominated stocks lose 5% in USD terms, but the short-yen forward gains ~5%. The two cancel. The fund captures only the underlying stock movement, which is the point.
      </p>
      <p>
        Hedging isn&rsquo;t free. Forward contracts embed the interest-rate differential between the two currencies (the &ldquo;carry cost&rdquo;). If the U.S. has 5% rates and Europe has 3%, a EUR/USD hedge costs ~2% annually. That&rsquo;s why hedged bond funds (BNDX at 0.07% expense) can still lag their unhedged twin (BWX at 0.35% expense) in some environments — the hedge cost eats into the already-thin bond yield.
      </p>

      <h2>Hedged wins when… Unhedged wins when…</h2>
      <p><strong>Hedged wins</strong> when the U.S. dollar strengthens. Every 1% move up in the DXY dollar index means an unhedged foreign fund gives back 1% just on translation, before any stock movement. The 2022 hiking cycle is the textbook case: DXY rose ~10%, and DBEF outperformed EFA by roughly that margin over the year.</p>
      <p><strong>Unhedged wins</strong> when the U.S. dollar weakens. The 2005-2007 weak-dollar cycle boosted EFA over DBEF by 15+ percentage points cumulatively. A tactical dollar-bear allocator will deliberately hold unhedged ISHG or BWX as a low-volatility way to profit from a falling greenback without needing to speculate in the currency futures market directly.</p>

      <h2>The three families that need family-specific benchmarks</h2>

      <h3>1. Developed-Markets Equity (EAFE)</h3>
      <p>
        &ldquo;EAFE&rdquo; = Europe, Australasia, Far East. It&rsquo;s the standard developed-ex-U.S.-and-Canada universe (Japan, UK, France, Germany, Switzerland, Netherlands, Australia, etc.). The MSCI EAFE Index is the gold-standard tracker, and it comes in three flavors you must not confuse:
      </p>
      <ul>
        <li><strong>Unhedged (EFA / IEFA / SPDW)</strong> — captures both stocks AND FX. The vanilla core allocation for most retail investors.</li>
        <li><strong>Hedged (DBEF / HEFA)</strong> — captures only stocks. Preferred by allocators who already have USD assets and don&rsquo;t want additional FX volatility.</li>
        <li><strong>Small-Cap (VSS)</strong> — small companies from the same universe. Higher volatility, different risk factor. Never compare small-cap to broad-cap benchmarks.</li>
      </ul>

      <h3>2. Emerging Markets Equity</h3>
      <p>
        Standard EM benchmarks (IEMG, VWO) are ~25-30% China-weighted, so a single Beijing policy shift dominates the fund&rsquo;s return. The ex-China variant (EMXC, XCEM, AVXC, PEMX, STXE, EMM) strips out China so you capture Taiwan / India / South Korea / Brazil / Mexico / Saudi Arabia purely.
      </p>
      <p>
        Then the same hedged / unhedged decision applies: <strong>DBEM / HEEM</strong> hedge the volatile EM currencies (Turkish Lira, Brazilian Real, South African Rand) back to USD; <strong>IEMG / VWO</strong> leave them exposed. EM currency swings are dramatically larger than developed-market swings, so the hedge decision matters more here.
      </p>

      <h3>3. International Bonds (three sub-categories)</h3>
      <p>
        Foreign bond funds are where the hedge decision matters most, because bond yields are small (2-4%) and FX swings are large (5-10% annually):
      </p>
      <ul>
        <li><strong>Broad Hedged (BNDX / IAGG)</strong> — the industry-standard core intl bond allocation. USD-hedged, ~7-year duration, ~2.5% yield. Behaves like a slightly-more-diversified U.S. Treasury fund.</li>
        <li><strong>Broad Unhedged (BWX)</strong> — same asset class, no hedge. Returns swing hard on the dollar cycle. Used as a tactical FX tool, not a core bond holding.</li>
        <li><strong>Short-Duration Unhedged (ISHG)</strong> — 1-3 year maturities. Duration is so short (~2 years) that FX movement dominates the return. Popular as a &ldquo;short the dollar&rdquo; low-volatility play.</li>
      </ul>
      <p>
        EM bonds split by currency of issuance rather than hedge status: <strong>EMB</strong> holds USD-denominated EM sovereign debt (no FX risk, only credit risk, yields 5-8%); <strong>EMLC</strong> holds local-currency debt (both credit AND FX risk, higher yields but more volatile).
      </p>

      <h2>Single-currency and FX-strategy funds — different beasts</h2>
      <p>
        Some ETFs don&rsquo;t hold stocks or bonds at all — they just hold cash/futures in a single foreign currency. These are pure FX products:
      </p>
      <ul>
        <li><strong>Invesco CurrencyShares series</strong>: FXA (Australian Dollar), FXB (British Pound), FXC (Canadian Dollar), FXE (Euro), FXF (Swiss Franc), FXY (Japanese Yen).</li>
        <li><strong>Invesco DB USD Index</strong>: UUP (long dollar, effectively short a G10 basket), UDN (short dollar).</li>
        <li><strong>WisdomTree strategies</strong>: CEW (EM currency basket), USDU (broad USD bullish alternative to UUP).</li>
      </ul>
      <p>
        These cannot be benchmarked against equity or bond ETFs. DiviDrip routes all single-currency and FX-strategy funds to a unified <strong>UUP / ELD / CEW</strong> trio so you see how the fund performed against the dollar-strength anchor, the EM-currency-and-rates blend, and the pure EM-currency basket.
      </p>

      <h2>Tax treatment — mostly boring, occasionally surprising</h2>
      <p>
        The overwhelming majority of currency-hedged and unhedged ETFs sit on standard <strong>Form 1099-DIV</strong> treatment — the same as any U.S. equity ETF. Long-term capital gains at 0/15/20% depending on income bracket. Short-term at your ordinary rate (up to 37%). Distributions may partly qualify for the qualified-dividend rate if the underlying stocks meet the holding-period test.
      </p>
      <p>
        The following funds all use standard 1099-DIV treatment:
      </p>
      <Div1099Table />
      <p>
        The occasional surprise is <strong>Section 1256 contracts</strong>. Some FX-strategy funds hold futures directly (Invesco DB currency series, WisdomTree Managed Futures). Section 1256 treats those futures as 60% long-term / 40% short-term REGARDLESS of holding period — so gains get a blended ~26.8% max federal rate (0.6 × 20% + 0.4 × 37%). Losses are also treated 60/40 and can be carried back three years, which is unusually generous. The trade-off is complexity: you file these on Form <strong>6781</strong> instead of the simpler 1099-DIV Schedule B path, and mark-to-market rules mean unrealized gains are taxed each year even without a sale.
      </p>
      <p>
        <strong>Foreign withholding tax</strong> is a separate wrinkle for funds that hold foreign stocks. When a European or Asian company pays a dividend into an ETF, the country of origin typically withholds 15-30% at the source. The fund passes this through as a &ldquo;foreign tax paid&rdquo; box on your 1099-DIV. If you hold in a TAXABLE account, you can recover most of it via the Foreign Tax Credit on Form 1116. If you hold in a Roth IRA, you LOSE the withholding entirely because the account can&rsquo;t claim credits it doesn&rsquo;t owe taxes for. This asymmetry is why international dividend funds are usually better in taxable accounts than in Roths, contrary to the usual &ldquo;dividends belong in Roth&rdquo; rule.
      </p>

      <h2>DiviDrip&rsquo;s family-benchmark routing</h2>
      <p>
        The Benchmark Performance card on every Stock Modal&rsquo;s Insights tab now runs a server-side classifier that reads each ticker&rsquo;s name, description, and asset type. Sixteen asset-class families are detected — developed hedged / unhedged / small-cap equity, EM ex-China / hedged / unhedged, Pacific ex-Japan, international REIT, three flavors of international bond, two flavors of EM bond, FX strategy, plus <em>crypto</em> and <em>delisted</em> sentinels. Each family maps to a curated 3-ETF benchmark trio and displays a &ldquo;<strong>Compared against: [Category]</strong>&rdquo; caption with a &ldquo;<strong>Why these?</strong>&rdquo; tooltip explaining the routing.
      </p>
      <p>
        The classifier fixes fundamentally wrong signals that used to plague the card. Before this feature, XCNY (EM ex-China) was benchmarked against SPY — a 50%+ apparent underperformance that was really an asset-class mismatch. FXB (British Pound) was benchmarked against SPY / SCHD / VYM despite being a pure currency ETF with no equity exposure. LQIG (a bond ETF that was quietly liquidated May 2026) was benchmarked against BNDX with stale data. All of these now route correctly or show a fallback banner.
      </p>
      <p>
        To try it yourself: open any international / EM / bond / REIT / FX ETF in the Stock Modal (say, EMXC or DBEF or FXB), switch to the Insights tab, and scroll to Benchmark Performance. You&rsquo;ll see the family caption at the top of the trio and can click through to the relevant Glossary entry for a deeper explanation of the routing.
      </p>

      <h2>Rules of thumb</h2>
      <ol>
        <li><strong>Bonds → almost always hedge.</strong> Foreign bond yields are small; FX volatility swamps them. BNDX beats BWX ~70% of years.</li>
        <li><strong>Core long-term equity → hedge is usually cleaner.</strong> You&rsquo;re holding for 10+ years; you don&rsquo;t want a decade&rsquo;s worth of FX volatility overlaid on your stock picks. DBEF over EFA as a core position.</li>
        <li><strong>Tactical dollar bet → go unhedged.</strong> If you have a strong view that the USD is going to weaken over the next 12-24 months, EFA / BWX / ISHG capture that thesis without needing to buy currency futures directly.</li>
        <li><strong>EM → hedge if you can&rsquo;t stomach ±30% currency swings.</strong> Turkish Lira dropped 40% in 2021. If you don&rsquo;t want that volatility in your EM allocation, DBEM / HEEM. Otherwise IEMG / VWO.</li>
        <li><strong>Single-currency ETFs are speculation, not investment.</strong> FXB, FXE, FXY don&rsquo;t compound like stocks; they just track a currency. Hold them for ≤ 12 months as a tactical FX position, not as core allocation.</li>
      </ol>

      <p className="mt-6 text-sm text-muted-foreground">
        Also see the <AbbrTip term="BENCHMARK_FAMILY_FX_STRATEGY">Glossary entry on Hedged vs Unhedged ETFs</AbbrTip> for the terse reference version, and the <AbbrTip term="BENCHMARK_FAMILY_INTL_BOND_HEDGED">International Bond ETF</AbbrTip>, <AbbrTip term="BENCHMARK_FAMILY_EM_EX_CHINA">Emerging Markets ex-China</AbbrTip>, and <AbbrTip term="BENCHMARK_FAMILY_INTL_REIT">International REIT ETF</AbbrTip> entries for the specific sub-family definitions.
      </p>
    </LearnPage>
  );
}
